/**
 * GUÍA DE LIQUIDACIÓN · piezas puras (GUI-02, GUI-05, GUI-10). Sin Angular, para probarlas con node --test.
 */
export type RolGuia = 'tesorero' | 'gerente' | 'verificador' | 'consulta';

export interface PreguntaGuia {
  id: string;
  pregunta: string;
  respuesta: string;
  roles: RolGuia[];
  /** Cuando la respuesta es lo que ve o recibe el comercio (A6). */
  comercio?: boolean;
}

export interface PasoGuia { titulo: string; quien: string; roles: RolGuia[]; }

export const ROLES: { id: RolGuia; nombre: string }[] = [
  { id: 'tesorero', nombre: 'Tesorero' },
  { id: 'gerente', nombre: 'Gerente o director' },
  { id: 'verificador', nombre: 'Verificador' },
  { id: 'consulta', nombre: 'Consulta' },
];

/**
 * GUI-14: el video de cada rol vive en Drive, «cualquiera con el enlace» (Mario, 25-oct R28), en la carpeta
 * «Videos de la Guía de liquidación» de Mario. El archivo es fijo por rol: nunca viene del navegador.
 */
export const VIDEOS_DRIVE: Record<RolGuia, string> = {
  tesorero: '10tTsDghp13AwftuWUtd8-cNxhzVOZ6MR',
  gerente: '1oaColdGs9OEdCLyVc3Z085tqm5cUxyEP',
  verificador: '11yfCk0GBMTZLaF0ysBIf36V-V2_sWEJ-',
  consulta: '1daBe20kwNrpgKcrQ08MZ6JXFum0Bn_DZ',
};

/** El reproductor de Drive para el rol, o null si el rol no tiene video. */
export const urlVideoDrive = (rol: string | null | undefined): string | null => {
  const id = rol && Object.prototype.hasOwnProperty.call(VIDEOS_DRIVE, rol) ? VIDEOS_DRIVE[rol as RolGuia] : '';
  return id ? `https://drive.google.com/file/d/${id}/preview` : null;
};

/** GUI-02: el rol por omisión según permisos; con varios gana el primero de esta lista. */
export const rolPorPermisos = (tienePermiso: (p: string) => boolean): RolGuia => {
  if (tienePermiso('liquidacion.aprobar')) return 'gerente';
  if (['liquidacion.preparar', 'liquidacion.lotes', 'liquidacion.cerrar_dia'].some((p) => tienePermiso(p))) return 'tesorero';
  if (tienePermiso('config.comercios_cuentas.verify')) return 'verificador';
  return 'consulta';
};

/** `?rol=` válido manda sobre el deducido; uno inválido se ignora (null). */
export const rolDesdeUrl = (v: string | null | undefined): RolGuia | null =>
  ROLES.some((r) => r.id === v) ? (v as RolGuia) : null;

const plano = (s: string): string => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** GUI-05: busca en pregunta y respuesta sin mayúsculas ni acentos; `todas` quita el filtro «De mi rol». */
export const filtrarPreguntas = (lista: PreguntaGuia[], f: { texto: string; rol: RolGuia; todas: boolean }): PreguntaGuia[] => {
  const q = plano(f.texto).trim();
  return (lista || []).filter((p) => (f.todas || p.roles.includes(f.rol)) && (!q || plano(`${p.pregunta} ${p.respuesta}`).includes(q)));
};

/** GUI-10: las seis etapas del manual por perfil, con quién hace cada una. */
export const PASOS: PasoGuia[] = [
  { titulo: 'Configurar el comercio', quien: 'Gerente o director · verificador de cuentas', roles: ['gerente', 'verificador'] },
  { titulo: 'Parámetros de liquidación', quien: 'Gerente o director · el tesorero los ve', roles: ['gerente'] },
  { titulo: 'Vender en la caja', quien: 'Comercio', roles: [] },
  { titulo: 'Liquidar: cierre y tandas', quien: 'Tesorero prepara · gerente o director firman', roles: ['tesorero', 'gerente'] },
  { titulo: 'Pagar por banco', quien: 'Tesorero · el banco responde', roles: ['tesorero'] },
  { titulo: 'El comercio cobra', quien: 'Administrador del comercio, en su portal', roles: [] },
];
